import React, { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { GLTF, GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";

interface ObjectProperties {
  name: string;
  sizeX: number;
  sizeY: number;
  sizeZ: number;
  vertices: number;
  triangles: number;
  volume: number;
  surface: number;
}

export const ThreeDView: React.FC = () => {
  const mountRef = useRef<HTMLDivElement | null>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const [selectedObject, setSelectedObject] = useState<ObjectProperties | null>(
    null
  );
  const [previousMesh, setPreviousMesh] = useState<THREE.Mesh | null>(null);

  useEffect(() => {
    if (!mountRef.current) return;

    const scene = new THREE.Scene();
    sceneRef.current = scene;
    const camera = new THREE.PerspectiveCamera(
      75,
      window.innerWidth / window.innerHeight,
      1,
      1000
    );
    cameraRef.current = camera;
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(window.innerWidth, window.innerHeight);
    mountRef.current.appendChild(renderer.domElement);

    // Lighting setup
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.7);
    scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
    directionalLight.position.set(1, 1, 1).normalize();
    scene.add(directionalLight);

    // OrbitControls setup
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.25;
    controls.enableZoom = true;

    // Raycaster for object selection
    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2();

    // Function to calculate object properties
    const calculateObjectProperties = (
      object: THREE.Mesh
    ): ObjectProperties => {
      const geometry = object.geometry;
      const positionAttribute = geometry.attributes.position;
      const vertexCount = positionAttribute.count;
      let surfaceArea = 0;
      let volume = 0;

      // Calculate surface area and volume using tetrahedrons
      if (geometry.index) {
        const indices = geometry.index.array;
        for (let i = 0; i < indices.length; i += 3) {
          const v1 = new THREE.Vector3().fromBufferAttribute(
            positionAttribute,
            indices[i]
          );
          const v2 = new THREE.Vector3().fromBufferAttribute(
            positionAttribute,
            indices[i + 1]
          );
          const v3 = new THREE.Vector3().fromBufferAttribute(
            positionAttribute,
            indices[i + 2]
          );

          // Calculate the area of the triangle for surface area
          const edge1 = new THREE.Vector3().subVectors(v2, v1);
          const edge2 = new THREE.Vector3().subVectors(v3, v1);
          const crossProduct = new THREE.Vector3().crossVectors(edge1, edge2);
          const triangleArea = 0.5 * crossProduct.length();
          surfaceArea += triangleArea;

          // Calculate the signed volume of the tetrahedron formed by v1, v2, v3, and the origin
          const tetrahedronVolume = v1.dot(crossProduct) / 6;
          volume += tetrahedronVolume;
        }
      } else {
        // Non-indexed geometry, iterate directly over vertices
        for (let i = 0; i < vertexCount; i += 3) {
          const v1 = new THREE.Vector3().fromBufferAttribute(
            positionAttribute,
            i
          );
          const v2 = new THREE.Vector3().fromBufferAttribute(
            positionAttribute,
            i + 1
          );
          const v3 = new THREE.Vector3().fromBufferAttribute(
            positionAttribute,
            i + 2
          );

          // Calculate the area of the triangle for surface area
          const edge1 = new THREE.Vector3().subVectors(v2, v1);
          const edge2 = new THREE.Vector3().subVectors(v3, v1);
          const crossProduct = new THREE.Vector3().crossVectors(edge1, edge2);
          const triangleArea = 0.5 * crossProduct.length();
          surfaceArea += triangleArea;

          // Calculate the signed volume of the tetrahedron formed by v1, v2, v3, and the origin
          const tetrahedronVolume = v1.dot(crossProduct) / 6;
          volume += tetrahedronVolume;
        }
      }

      // Take the absolute value of the volume (as it could be negative due to the order of vertices)
      volume = Math.abs(volume);

      // Calculate bounding box size for other properties
      geometry.computeBoundingBox();
      const boundingBox = geometry.boundingBox;
      const size = new THREE.Vector3();
      boundingBox?.getSize(size);

      const triangles = geometry.index
        ? geometry.index.count / 3
        : vertexCount / 3;

      const properties = {
        name: object.name || "Unnamed Object",
        sizeX: size.x,
        sizeY: size.y,
        sizeZ: size.z,
        vertices: vertexCount,
        triangles: triangles,
        volume: volume,
        surface: surfaceArea,
      };

      return properties;
    };

    // Event listener for mouse clicks
    const onMouseClick = (event: MouseEvent) => {
      scene.traverse((child) => {
        if (child instanceof THREE.Mesh && child.material) {
          (child.material as THREE.MeshStandardMaterial).color.setHex(0xffffff);
        }
      });
      // Normalize mouse coordinates
      const rect = mountRef.current?.getBoundingClientRect();
      if (!rect) return;

      mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

      raycaster.setFromCamera(mouse, camera);
      const intersects = raycaster.intersectObjects(scene.children, true);

      if (intersects.length > 0) {
        const selectedMesh = intersects[0].object as THREE.Mesh;

        if (selectedMesh.material) {
          // Set the clicked object to light blue
          (selectedMesh.material as THREE.MeshStandardMaterial).color.setHex(
            0x87CEEB
          );

          // Calculate and set the selected object properties
          const properties = calculateObjectProperties(selectedMesh);
          setSelectedObject(properties);
          setPreviousMesh(selectedMesh); // Update the previous mesh to the currently selected one
        } else {
        }
      } else {
        // Reset all objects to their original color if clicking on empty space
        scene.traverse((child) => {
          if (child instanceof THREE.Mesh && child.material) {
            (child.material as THREE.MeshStandardMaterial).color.setHex(
              0xffffff
            );
          }
        });
        setSelectedObject(null);
        setPreviousMesh(null);
      }
    };

    window.addEventListener("click", onMouseClick);

    // Load the 3D model
    const loader = new GLTFLoader();
    loader.load(
      "https://storage.googleapis.com/kal-sense-public-bucket-dev-v01/file_2.gltf",
      (gltf: GLTF) => {
        const model = gltf.scene;

        model.traverse((child) => {
          if (child instanceof THREE.Mesh) {
            if (child.material) {
              child.material = new THREE.MeshStandardMaterial({
                color: 0xffffff,
                metalness: 0.5,
                roughness: 0.5,
              });
            }
          }
        });

        const box = new THREE.Box3().setFromObject(model);
        const center = box.getCenter(new THREE.Vector3());
        model.position.sub(center);

        scene.add(model);
        scene.background = new THREE.Color(0xf0f0f0);

        const size = box.getSize(new THREE.Vector3());
        const maxDim = Math.max(size.x, size.y, size.z);
        const fov = camera.fov * (Math.PI / 180);
        let cameraZ = Math.abs(maxDim / 2 / Math.tan(fov / 2));
        cameraZ *= 1.5;
        camera.position.z = cameraZ;

        controls.maxDistance = cameraZ * 2;
        controls.target.set(0, 0, 0);
        controls.update();
      },
      (xhr) => {
        console.log((xhr.loaded / xhr.total) * 100 + "% loaded");
      },
      (error) => {
        console.error("An error happened while loading the model");
      }
    );

    const animate = () => {
      requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    const handleResize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      renderer.setSize(width, height);
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
    };
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("click", onMouseClick);
      mountRef.current?.removeChild(renderer.domElement);
    };
  }, []);

  return (
    <div className="relative w-full h-[100vh]">
      <div className="absolute top-0 left-0 z-10 w-[300px] bg-white p-4">
        <h3>{selectedObject?.name || "No object selected"}</h3>
        <p>Size X: {selectedObject?.sizeX.toFixed(2) || "-"}</p>
        <p>Size Y: {selectedObject?.sizeY.toFixed(2) || "-"}</p>
        <p>Size Z: {selectedObject?.sizeZ.toFixed(2) || "-"}</p>
        <p>Vertices: {selectedObject?.vertices || "-"}</p>
        <p>Triangles: {selectedObject?.triangles || "-"}</p>
        <p>Volume: {selectedObject?.volume.toFixed(2) || "-"}</p>
        <p>Surface Area: {selectedObject?.surface.toFixed(2) || "-"}</p>
      </div>
      <div ref={mountRef} className="w-full h-full border-l-2 border-black" />
    </div>
  );
};
